/**
 * <s-collection-rail> — horizontal scroll-snap rail with line dots and prev/next arrows.
 *
 * Required markup:
 *   [data-rail-track]                 — scrollable track.
 *   [data-rail-item]                  — slides, direct children of the track.
 *   button[command="--prev|--next"][commandfor=<this id>] — arrows (disabled at the ends).
 *   button[command="--go"][data-index][commandfor=<this id>] — dots; dots past the last
 *     reachable start position are hidden so every dot maps to a real stop.
 */
class SCollectionRail extends HTMLElement {
  #ac;
  #resizeObserver;
  #rafId = 0;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', this.#handleCommand, opts);

    const track = this.#track;
    if (!track) return;

    track.addEventListener('scroll', this.#scheduleSync, { ...opts, passive: true });
    this.#resizeObserver = new ResizeObserver(this.#scheduleSync);
    this.#resizeObserver.observe(track);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#rafId);
  }

  get #track() {
    return this.querySelector('[data-rail-track]');
  }

  get #items() {
    const track = this.#track;
    return track ? Array.from(track.querySelectorAll(':scope > [data-rail-item]')) : [];
  }

  /** Distance between the starts of two neighbouring items. */
  #step() {
    const items = this.#items;
    if (items.length < 2) return items[0]?.offsetWidth || 1;
    return Math.abs(items[1].offsetLeft - items[0].offsetLeft) || 1;
  }

  /** Highest index an item can be scrolled to as the first visible card. */
  #maxIndex() {
    const track = this.#track;
    if (!track) return 0;
    const maxScroll = track.scrollWidth - track.clientWidth;
    return Math.max(0, Math.round(maxScroll / this.#step()));
  }

  #handleCommand = (event) => {
    const max = this.#maxIndex();
    if (event.command === '--next') {
      this.#goTo(Math.min(this.#index + 1, max));
    } else if (event.command === '--prev') {
      this.#goTo(Math.max(this.#index - 1, 0));
    } else if (event.command === '--go') {
      const target = Number(event.source?.dataset.index);
      if (Number.isInteger(target)) this.#goTo(Math.min(Math.max(target, 0), max));
    }
  };

  #goTo(index) {
    const track = this.#track;
    if (!track) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const left = index * this.#step() * (isRtl ? -1 : 1);
    track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
  }

  #scheduleSync = () => {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#sync());
  };

  #sync() {
    const track = this.#track;
    if (!track) return;

    const max = this.#maxIndex();
    const scrolled = Math.abs(track.scrollLeft);
    const atEnd = scrolled >= track.scrollWidth - track.clientWidth - 2;
    this.#index = atEnd ? max : Math.min(max, Math.round(scrolled / this.#step()));

    this.querySelectorAll('[command="--go"][data-index]').forEach((dot) => {
      const i = Number(dot.dataset.index);
      dot.hidden = i > max;
      if (i === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    const prev = this.querySelector('[command="--prev"]');
    const next = this.querySelector('[command="--next"]');
    if (prev) prev.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= max;

    // Nothing to scroll (wide screen, few items): hide the controls entirely.
    const controls = this.querySelector('[data-rail-dots]')?.parentElement;
    if (controls) controls.hidden = max === 0;
  }
}

if (!customElements.get('s-collection-rail')) {
  customElements.define('s-collection-rail', SCollectionRail);
}
